import { useBrandingContext } from "context/BrandingContext";
import { useColorScheme } from "context/ColorSchemeContext";
import DefaultLightSymbol from "../assets/images/OutpaveiconPrimary.svg";
import DefaultDarkSymbol from "../assets/images/OutpaveiconSecondary.svg";

/**
 * Logo choice used to resolve a named background out of the DOM and measure its
 * luminance. It no longer measures anything (OUT-1393). The surfaces these sit
 * on are the package's canvas, derived from the tenant's own colour through
 * `identity.background`, so they flip with the scheme by construction and the
 * scheme is the whole answer.
 */
export const useLogoForScheme = (): string => {
  const { logos } = useBrandingContext();
  const { isDark } = useColorScheme();
  return isDark ? logos.lightLogo : logos.logo;
};

/**
 * The brand MARK, for places too narrow for the wordmark (the collapsed nav
 * rail). A company's uploaded `logos.icon` wins when it exists; it defaults to
 * an empty string, so falling through to the Outpave mark is the normal path.
 */
export const useSymbolForScheme = (): string => {
  const { logos } = useBrandingContext();
  const { isDark } = useColorScheme();
  if (logos.icon) return logos.icon;
  return isDark ? DefaultLightSymbol : DefaultDarkSymbol;
};

export type ThemeColors = {
  mainWhite?: string;
  mainBlack?: string;

  primary?: string;
  primaryAccent?: string;
  tertiary?: string;
  primaryDark?: string;
  secondary?: string;
  secondaryAccent?: string;
  secondaryDark?: string;

  gradientStart?: string;
  gradientEnd?: string;

  // The PAYMENT card's face. Named in full because "card" is overloaded here:
  // `cardBackground` / `cardTextPrimary` / `cardSubText` below are the legacy
  // UI-card surface tokens and mean something else entirely.
  paymentCardBackground?: string;
  /**
   * Which end of the package's ink pair the card face uses: "light" or "dark",
   * not a colour. Only artwork needs it, since a solid face is derived with
   * `readableOnHex`. Stored as a choice so the values stay the DS's to change.
   */
  paymentCardTextMode?: string;
  /**
   * Which artwork the card face wears: "own" (their upload), "default" (the
   * platform's, or the product's when the platform has none) or "none" (no
   * artwork, so the ground is the whole face). Unset behaves as "own" when they
   * have an upload and "default" otherwise, which is what every company saw
   * before the choice existed.
   */
  paymentCardArt?: string;
  /** "stock", "brand" or "background" under any artwork; unset keeps the older art and background rules. */
  paymentCardFace?: string;

  success?: string;
  successLight?: string;
  warning?: string;
  warningLight?: string;
  danger?: string;
  info?: string;
  infoLight?: string;

  // Not colours. The package's chart knob, stored on the same record because a
  // branding record is a flat {key, name} list.
  chartStrategy?: string;
  chartLeadWithSecondary?: string;
  /** Chart slot 3: "0" is the brand tertiary, "1" to "6" a generated companion. */
  chartThirdColor?: string;

  textPrimary?: string;
  textSecondary?: string;
  textHeader?: string;
  subText?: string;
  textLight?: string;
  textBackground?: string;

  navBackground?: string;
  navBackgroundHover?: string;
  navBackgroundActive?: string;
  navSecondaryBackground?: string;
  navSecondaryBackgroundHover?: string;
  navSecondaryBackgroundActive?: string;
  navText?: string;
  navTextActive?: string;
  navSearchBackground?: string;

  footerText?: string;
  footerTextDash?: string;
  footerBackgroundDash?: string;
  footerBackground?: string;

  dashboardCardBackground?: string;
  dashboardCardBackgroundHover?: string;
  dashboardCardText?: string;
  dashboardCardTextNumber?: string;
  dashboardCardSubText?: string;

  pageBackground?: string;
  pageTitleText?: string;
  pageSubTitleText?: string;

  cardBackground?: string;
  cardHoverBackground?: string;
  cardTextPrimary?: string;
  cardTextHeader?: string;
  cardSubText?: string;

  drawerBackground?: string;
  drawerFooterBackground?: string;
  drawerHeaderBackground?: string;
  drawerHeaderText?: string;

  tableBackground?: string;
  tableHeadText?: string;
  tableBodyText?: string;

  strokeBorder?: string;
  strokeBorderDark?: string;
  strokeBorderLight?: string;
};

// Core colors that should always be defined in a theme
